import {
  ApiTestEndpoint,
  buildRequestDetails,
  isSessionOnlyApiPath,
  pathParamNames,
} from './apiTestRequest';

const POSTMAN_SCHEMA = 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json';

export interface PostmanCollection {
  info: {
    name: string;
    description: string;
    schema: string;
  };
  variable: Array<{ key: string; value: string }>;
  auth: PostmanAuth;
  item: Array<{
    name: string;
    item: PostmanRequestItem[];
  }>;
}

type PostmanAuthEntry = { key: string; value: string; type: string };

type PostmanAuth =
  | { type: 'noauth' }
  | { type: 'apikey'; apikey: PostmanAuthEntry[] }
  | { type: 'bearer'; bearer: PostmanAuthEntry[] };

const API_KEY_AUTH: PostmanAuth = {
  type: 'apikey',
  apikey: [
    { key: 'key', value: 'X-API-Key', type: 'string' },
    { key: 'value', value: '{{apiKey}}', type: 'string' },
    { key: 'in', value: 'header', type: 'string' },
  ],
};

const BEARER_AUTH: PostmanAuth = {
  type: 'bearer',
  bearer: [{ key: 'token', value: '{{idToken}}', type: 'string' }],
};

interface PostmanRequestItem {
  name: string;
  request: {
    method: string;
    header: Array<{ key: string; value: string; disabled?: boolean; description?: string }>;
    description: string;
    url: {
      raw: string;
      host: string[];
      path: string[];
      query?: Array<{
        key: string;
        value: string;
        description?: string;
        disabled?: boolean;
      }>;
      variable?: Array<{ key: string; value: string; description?: string }>;
    };
    body?: {
      mode: 'raw';
      raw: string;
      options?: { raw: { language: string } };
    };
    auth?: PostmanAuth;
  };
}

export interface BuildPostmanCollectionOptions {
  endpoints: ApiTestEndpoint[];
  baseUrl: string;
  selectedEndpointId?: string;
  paramValues?: Record<string, string>;
  requestBody?: string;
  /** Scope: selected request only, or every documented endpoint. */
  mode?: 'selected' | 'all';
  /** Prefills the collection `apiKey` variable when the tester has a key selected. */
  apiKey?: string;
  organizationId?: string;
}

function valuesForEndpoint(
  endpoint: ApiTestEndpoint,
  options: BuildPostmanCollectionOptions
): { paramValues: Record<string, string>; bodyText: string } {
  const isSelected = options.selectedEndpointId === endpoint.id;
  if (isSelected) {
    return {
      paramValues: options.paramValues || {},
      bodyText: options.requestBody || '',
    };
  }

  const paramValues: Record<string, string> = {};
  endpoint.parameters?.forEach((param) => {
    if (param.example) {
      paramValues[param.name] = String(param.example);
    }
  });

  let bodyText = '';
  if (endpoint.body) {
    const defaultBody: Record<string, unknown> = {};
    endpoint.body.forEach((field) => {
      if (field.example !== undefined) {
        defaultBody[field.name] = field.example;
      }
    });
    bodyText = JSON.stringify(defaultBody, null, 2);
  }

  return { paramValues, bodyText };
}

function usesSessionAuth(endpoint: ApiTestEndpoint): boolean {
  return isSessionOnlyApiPath(endpoint.path) || (endpoint.requiresAuth && !endpoint.requiresApiKey);
}

function endpointDescription(endpoint: ApiTestEndpoint): string {
  const parts = [endpoint.description];
  if (endpoint.orgScoped) {
    parts.push(
      'Organization is not a query parameter. The API scopes this call to the organization on the API key (or the impersonated store org when calling from a Motzu admin session).'
    );
  }
  if (usesSessionAuth(endpoint)) {
    parts.push(
      'This route requires a Motzu login. Set the collection variable `idToken` to a Firebase ID token and send it as Authorization: Bearer.'
    );
  } else if (endpoint.requiresApiKey || endpoint.requiresAuth) {
    parts.push('Set the collection variable `apiKey` and send it as the X-API-Key header.');
  }
  return parts.join('\n\n');
}

function requestAuth(endpoint: ApiTestEndpoint): PostmanAuth {
  if (!endpoint.requiresAuth && !endpoint.requiresApiKey) {
    return { type: 'noauth' };
  }
  if (usesSessionAuth(endpoint)) {
    return BEARER_AUTH;
  }
  return API_KEY_AUTH;
}

function authHeaders(
  endpoint: ApiTestEndpoint
): Array<{ key: string; value: string; disabled?: boolean; description?: string }> {
  if (!endpoint.requiresAuth && !endpoint.requiresApiKey) {
    return [];
  }
  if (usesSessionAuth(endpoint)) {
    return [
      {
        key: 'Authorization',
        value: 'Bearer {{idToken}}',
        disabled: true,
        description:
          'Documented only — runtime auth is request.auth bearer {{idToken}}. Leave disabled so Postman does not send a duplicate Authorization header.',
      },
    ];
  }
  return [
    {
      key: 'X-API-Key',
      value: '{{apiKey}}',
      description: 'Store API key. Set the collection variable `apiKey`.',
    },
  ];
}

function toPostmanItem(
  endpoint: ApiTestEndpoint,
  paramValues: Record<string, string>,
  bodyText: string
): PostmanRequestItem {
  const details = buildRequestDetails(endpoint, paramValues, bodyText);
  const names = pathParamNames(endpoint.path);
  const pathSet = new Set(names);

  const pathSegments = endpoint.path
    .split('/')
    .filter(Boolean)
    .map((segment) => (segment.startsWith(':') ? `:${segment.slice(1)}` : segment));

  const urlVariables = names.map((name) => ({
    key: name,
    value: paramValues[name] || '',
    description: endpoint.parameters?.find((p) => p.name === name)?.description,
  }));

  const query = (endpoint.parameters || [])
    .filter((param) => !pathSet.has(param.name.replace(/^:/, '')))
    .map((param) => {
      const name = param.name.replace(/^:/, '');
      const value = paramValues[name] || '';
      return {
        key: name,
        value,
        description: param.description,
        disabled: !value && !param.required,
      };
    });

  const rawPath = pathSegments
    .map((segment) => (segment.startsWith(':') ? `{{${segment.slice(1)}}}` : segment))
    .join('/');
  const enabledQuery = query.filter((q) => !q.disabled && q.value);
  const queryString = enabledQuery
    .map((q) => `${encodeURIComponent(q.key)}=${encodeURIComponent(q.value)}`)
    .join('&');
  const raw = queryString
    ? `{{baseUrl}}/${rawPath}?${queryString}`
    : `{{baseUrl}}/${rawPath}`;

  const header: PostmanRequestItem['request']['header'] = [...authHeaders(endpoint)];
  if (endpoint.orgScoped) {
    header.push({
      key: 'X-Impersonated-Organization-Id',
      value: '{{organizationId}}',
      disabled: true,
      description:
        'Platform admins only. Leave disabled when using an API key — org is taken from the key.',
    });
  }

  const item: PostmanRequestItem = {
    name: `${endpoint.method} ${endpoint.path}`,
    request: {
      method: endpoint.method,
      header,
      auth: requestAuth(endpoint),
      description: endpointDescription(endpoint),
      url: {
        raw,
        host: ['{{baseUrl}}'],
        path: pathSegments,
        ...(query.length > 0 ? { query } : {}),
        ...(urlVariables.length > 0 ? { variable: urlVariables } : {}),
      },
    },
  };

  if (details.parsedBody !== undefined && endpoint.method !== 'GET') {
    const rawBody =
      typeof details.parsedBody === 'string'
        ? details.parsedBody
        : JSON.stringify(details.parsedBody, null, 2);
    header.unshift({ key: 'Content-Type', value: 'application/json' });
    item.request.body = {
      mode: 'raw',
      raw: rawBody,
      options: { raw: { language: 'json' } },
    };
  }

  return item;
}

export function buildPostmanCollection(options: BuildPostmanCollectionOptions): PostmanCollection {
  const mode = options.mode || 'all';
  let endpoints = options.endpoints;
  if (mode === 'selected') {
    endpoints = options.endpoints.filter((e) => e.id === options.selectedEndpointId);
  }

  const grouped = new Map<string, ApiTestEndpoint[]>();
  endpoints.forEach((endpoint) => {
    const list = grouped.get(endpoint.category) || [];
    list.push(endpoint);
    grouped.set(endpoint.category, list);
  });

  const item = Array.from(grouped.entries()).map(([category, group]) => ({
    name: category,
    item: group.map((endpoint) => {
      const { paramValues, bodyText } = valuesForEndpoint(endpoint, options);
      return toPostmanItem(endpoint, paramValues, bodyText);
    }),
  }));

  return {
    info: {
      name: 'Motzu Product Data API',
      description:
        'Generated from Motzu API Testing. Set `apiKey` for store API routes (X-API-Key). Set `idToken` to a Firebase ID token for session-only routes such as /api-keys. Store override routes are scoped to the API key organization (organizationId is not a query parameter).',
      schema: POSTMAN_SCHEMA,
    },
    variable: [
      { key: 'baseUrl', value: options.baseUrl.replace(/\/$/, '') },
      { key: 'apiKey', value: options.apiKey || '' },
      { key: 'idToken', value: '' },
      { key: 'organizationId', value: options.organizationId || '' },
    ],
    auth: API_KEY_AUTH,
    item,
  };
}

export function downloadJsonFile(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Collection generated from the OpenAPI spec (openapi.yaml at the repo root,
// copied to src/generated/openapi.json by `npm run openapi:generate`).
// ---------------------------------------------------------------------------

type OpenApiSchema = Record<string, any>;

export interface OpenApiSpecLike {
  info: { title: string; description?: string; version: string };
  paths: Record<string, Record<string, any>>;
  components?: { schemas?: Record<string, OpenApiSchema> };
}

export interface BuildPostmanFromSpecOptions {
  spec: OpenApiSpecLike;
  baseUrl: string;
  /** Prefills the collection `apiKey` variable when the tester has a key selected. */
  apiKey?: string;
  /** Spec paths (OpenAPI form, e.g. `/products/{id}/rebates`) to leave out, such as a flag-gated group whose flag is off. */
  omitPaths?: string[];
}

function resolveSchema(spec: OpenApiSpecLike, schema: OpenApiSchema | undefined): OpenApiSchema {
  let current = schema || {};
  for (let i = 0; i < 8 && typeof current.$ref === 'string'; i += 1) {
    current = spec.components?.schemas?.[current.$ref.replace('#/components/schemas/', '')] || {};
  }
  return current;
}

/** A small example value for a schema: required properties only, depth limited. */
export function exampleFromSchema(spec: OpenApiSpecLike, schema: OpenApiSchema | undefined, depth = 0): unknown {
  const s = resolveSchema(spec, schema);
  if (s.example !== undefined) return s.example;
  if (s.default !== undefined) return s.default;
  if (Array.isArray(s.enum)) return s.enum.find((v: unknown) => v !== null);
  if (Array.isArray(s.allOf)) {
    return Object.assign({}, ...s.allOf.map((part: OpenApiSchema) => exampleFromSchema(spec, part, depth + 1)));
  }
  if (Array.isArray(s.oneOf)) return exampleFromSchema(spec, s.oneOf[0], depth + 1);
  switch (s.type) {
    case 'string':
      return s.format === 'uri' ? 'https://example.com/webhooks/motzu' : 'string';
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return depth > 3 ? [] : [exampleFromSchema(spec, s.items, depth + 1)];
    case 'object': {
      if (depth > 3) return {};
      const out: Record<string, unknown> = {};
      const required: string[] = s.required || [];
      Object.entries<OpenApiSchema>(s.properties || {}).forEach(([key, value]) => {
        if (required.includes(key)) out[key] = exampleFromSchema(spec, value, depth + 1);
      });
      return out;
    }
    default:
      return undefined;
  }
}

function specPostmanItem(spec: OpenApiSpecLike, path: string, method: string, op: any): PostmanRequestItem {
  const params: any[] = op.parameters || [];
  const pathParams = params.filter((p) => p.in === 'path');
  const queryParams = params.filter((p) => p.in === 'query');
  const segments = path
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.replace(/^\{(.+)\}$/, ':$1'));
  const isPublic = Array.isArray(op.security) && op.security.length === 0;

  const header: PostmanRequestItem['request']['header'] = isPublic
    ? []
    : [{ key: 'X-API-Key', value: '{{apiKey}}', description: 'API key from the API Keys page. Set the collection variable `apiKey`.' }];

  const item: PostmanRequestItem = {
    name: `${method.toUpperCase()} ${path.replace(/\{([^}]+)\}/g, ':$1')}`,
    request: {
      method: method.toUpperCase(),
      header,
      auth: isPublic ? { type: 'noauth' } : API_KEY_AUTH,
      description: [op.summary, op.description].filter(Boolean).join('\n\n'),
      url: {
        raw: `{{baseUrl}}/${segments.join('/')}`,
        host: ['{{baseUrl}}'],
        path: segments,
        ...(queryParams.length
          ? {
              query: queryParams.map((p) => ({
                key: p.name,
                value: p.schema?.default !== undefined ? String(p.schema.default) : '',
                description: p.description,
                disabled: !p.required,
              })),
            }
          : {}),
        ...(pathParams.length
          ? { variable: pathParams.map((p) => ({ key: p.name, value: '', description: p.description })) }
          : {}),
      },
    },
  };

  const bodySchema = op.requestBody?.content?.['application/json']?.schema;
  if (bodySchema) {
    header.unshift({ key: 'Content-Type', value: 'application/json' });
    item.request.body = {
      mode: 'raw',
      raw: JSON.stringify(exampleFromSchema(spec, bodySchema) ?? {}, null, 2),
      options: { raw: { language: 'json' } },
    };
  }
  return item;
}

/** The whole API-key surface of the spec as a Postman v2.1 collection, grouped by tag. */
export function buildPostmanCollectionFromSpec(options: BuildPostmanFromSpecOptions): PostmanCollection {
  const { spec } = options;
  const omitted = new Set(options.omitPaths || []);
  const groups = new Map<string, PostmanRequestItem[]>();
  Object.entries(spec.paths).forEach(([path, methods]) => {
    if (omitted.has(path)) return;
    Object.entries(methods).forEach(([method, op]) => {
      const tag = (op.tags && op.tags[0]) || 'API';
      const list = groups.get(tag) || [];
      list.push(specPostmanItem(spec, path, method, op));
      groups.set(tag, list);
    });
  });

  return {
    info: {
      name: `${spec.info.title} v${spec.info.version}`,
      description:
        'Generated from the Motzu OpenAPI spec. Set the collection variable `apiKey` (X-API-Key). API-key responses carry X-RateLimit-Limit, X-RateLimit-Remaining and X-RateLimit-Reset, and Retry-After on a 429.',
      schema: POSTMAN_SCHEMA,
    },
    variable: [
      { key: 'baseUrl', value: options.baseUrl.replace(/\/$/, '') },
      { key: 'apiKey', value: options.apiKey || '' },
    ],
    auth: API_KEY_AUTH,
    item: Array.from(groups.entries()).map(([name, item]) => ({ name, item })),
  };
}
